import React, { useState, useMemo } from 'react'

import { LinkOutlined } from '@ant-design/icons'
import { MultipleValue } from '@shared/table/Rules/component'
import TableIndex from '@shared/table/TableIndex'
import type { TableService, SupportRule } from '@shared/table/TableIndex'
import { Tag } from 'antd'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

interface MerchantOption {
  id: string;
  name: string;
}

interface SupplierListViewProps {
  service: TableService;
  role?: 'admin' | 'operator';
  showMerchantColumn?: boolean;
  merchants?: MerchantOption[];
}

interface SupplierRow {
  id: string;
  name: string;
  code?: string | null;
  merchant_name?: string | null;
  contact_name?: string | null;
  contact_phone?: string | null;
  payment_terms?: string | null;
  activated?: boolean;
  [key: string]: unknown;
}

const SupplierListView: React.FC<SupplierListViewProps> = ({
  service,
  role = 'admin',
  showMerchantColumn = false,
  merchants = [],
}) => {
  const { t } = useTranslation()
  const [lastModified, setLastModified] = useState(0)

  const columns = useMemo(() => {
    const cols: unknown[] = [
      {
        nameKey: 'models.supplier.name',
        persist: true,
        sortable: true,
        sortFieldName: 'suppliers.name',
        wrap: true,
        cell: ({ id, name }: SupplierRow) => (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Link to={`/suppliers/${id}`} style={{ wordBreak: 'break-word' }}>
              {name}
            </Link>
            <a
              href={`/${role}/suppliers/${id}`}
              target="_blank"
              onClick={(e) => e.stopPropagation()}
              style={{ flexShrink: 0 }} rel="noreferrer"
            >
              <LinkOutlined style={{ fontSize: '12px', color: '#999' }} />
            </a>
          </div>
        ),
      },
      {
        nameKey: 'models.supplier.code',
        sortable: true,
        sortFieldName: 'suppliers.code',
        wrap: true,
        selector: ({ code }: SupplierRow) => code || '-',
      },
    ]

    if (showMerchantColumn) {
      cols.push({
        nameKey: 'models.supplier.merchant',
        width: '150px',
        wrap: true,
        selector: ({ merchant_name }: SupplierRow) => merchant_name || '-',
      })
    }

    cols.push(
      {
        nameKey: 'models.supplier.contact_name',
        wrap: true,
        selector: ({ contact_name }: SupplierRow) => contact_name || '-',
      },
      {
        nameKey: 'models.supplier.contact_phone',
        wrap: true,
        selector: ({ contact_phone }: SupplierRow) => contact_phone || '-',
      },
      {
        nameKey: 'models.supplier.payment_terms',
        wrap: true,
        selector: ({ payment_terms }: SupplierRow) => payment_terms || '-',
      },
      {
        nameKey: 'models.supplier.activated',
        width: '100px',
        cell: ({ activated }: SupplierRow) => (
          <Tag color={activated ? 'green' : 'red'}>
            {activated ? t('common.status.active') : t('common.status.inactive')}
          </Tag>
        ),
      },
    )

    return cols
  }, [t, role, showMerchantColumn])

  const supportRules = useMemo(() => {
    const rules: SupportRule[] = []

    if (showMerchantColumn && merchants.length > 0) {
      rules.push({
        label: t('models.supplier.merchant'),
        component: MultipleValue,
        inputName: 'merchant_id',
        createable: false,
        defaultOptions: merchants.map((m) => ({
          label: m.name,
          value: m.id,
        })),
      })
    }

    return rules
  }, [t, showMerchantColumn, merchants])

  return (
    <TableIndex
      columns={columns}
      searchable={{
        match: 'contains',
        searchFields: [
          t('models.supplier.name'),
          t('models.supplier.code'),
        ],
      }}
      columnsEditable={true}
      tableId={`${role}SuppliersTable`}
      defaultQuery={{
        sorting_field: 'suppliers.id',
        sorting_direction: 'desc',
      }}
      lastModified={lastModified}
      setLastModified={setLastModified}
      service={service}
      supportRules={supportRules}
    />
  )
}

export default SupplierListView
